import React, { useState } from 'react'
import { X, Share2, Loader2, CheckCircle2, Users, Mail } from 'lucide-react'
import { shareApi } from '../api/lessonBuilderApi'

function currentUserRole(): string {
  try {
    const u = JSON.parse(localStorage.getItem('auth_user') || '{}')
    return u?.role || 'student'
  } catch {
    return 'student'
  }
}

interface Props {
  resourceType: 'lesson' | 'course'
  resourceId: string
  resourceTitle?: string
  onClose: () => void
}

const ShareModal: React.FC<Props> = ({ resourceType, resourceId, resourceTitle, onClose }) => {
  const isAdmin = currentUserRole() === 'admin'
  const [scope, setScope] = useState<'teacher' | 'all'>(isAdmin ? 'all' : 'teacher')
  const [email, setEmail] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setError(null)
    if (scope === 'teacher' && !email.trim()) {
      setError('Nhập email giáo viên nhận chia sẻ.')
      return
    }
    setBusy(true)
    try {
      await shareApi.share({
        resource_type: resourceType,
        resource_id: resourceId,
        scope,
        target_email: scope === 'teacher' ? email.trim() : undefined,
      })
      setDone(true)
    } catch (e) {
      setError((e as Error)?.message || 'Chia sẻ thất bại')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Share2 className="text-blue-600" size={20} />
            <h2 className="text-lg font-black text-gray-900">Chia sẻ {resourceType === 'course' ? 'khoá học' : 'bài giảng'}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700">
            <X size={22} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {resourceTitle && <p className="text-sm text-gray-500">"{resourceTitle}"</p>}

          {done ? (
            <div className="flex flex-col items-center py-6 text-emerald-600">
              <CheckCircle2 size={40} className="mb-2" />
              <p className="font-semibold">Đã chia sẻ thành công!</p>
              <button onClick={onClose} className="mt-4 px-4 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-semibold">
                Đóng
              </button>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {isAdmin && (
                  <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${scope === 'all' ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200'}`}>
                    <input type="radio" checked={scope === 'all'} onChange={() => setScope('all')} />
                    <Users size={18} className="text-blue-600" />
                    <span className="text-sm font-semibold text-gray-800">Toàn bộ giáo viên (chỉ admin)</span>
                  </label>
                )}
                <label className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer ${scope === 'teacher' ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200'}`}>
                  <input type="radio" checked={scope === 'teacher'} onChange={() => setScope('teacher')} />
                  <Mail size={18} className="text-blue-600" />
                  <span className="text-sm font-semibold text-gray-800">Một giáo viên (qua email)</span>
                </label>
              </div>

              {scope === 'teacher' && (
                <input
                  className="w-full p-2.5 border border-gray-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200"
                  placeholder="email-giao-vien@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}

              <button
                onClick={submit}
                disabled={busy}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-bold hover:bg-blue-700 disabled:opacity-50"
              >
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Chia sẻ
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ShareModal
